import {AddBuildingCommand} from "../../commands/addBuilding.command";
import {RemoveBuildingCommand} from "../../commands/removeBuilding.command";
import {UpdateBuildingCommand} from "../../commands/updateBuilding.command";
import {useIntegrator} from "../../integrator.context";
import {AddBuildingAttributeCommand} from "../../commands/addBuildingAttribute.command";
import {RemoveBuildingAttributeCommand} from "../../commands/removeBuildingAttribute.command";
import {Metadata} from "../../models/remoteConstants.model";
import {AddBuildingPassiveRewardCommand} from "../../commands/addBuildingPassiveReward.command";
import PassiveRewardCard from "./passiveRewardCard";
import {DropTargetMonitor, useDrag, useDrop} from "react-dnd";
import LocalizationInput from "../../elements/localizationInput.element";
import LocalizationTextArea from "../../elements/localizationTextArea.element";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import MetadataLabel from "../../elements/metadataLabel.element";
import EntityLabel from "../../elements/entityLabel";
import BooleanInput from "../../elements/booleanInput.element";
import IconButton from "../../elements/IconButton";
import EntityDragLabel from "../../elements/entityDragLabel";
import ImagePackElement from "../../elements/imagePack.element";
import List from "../../elements/list";
import Collapsible from "../../elements/collapsible";
import {useApp} from "../../app.context";
import {useEffect, useRef, useState} from "react";
import PromptDialog from "../../elements/promptDialog.element";
import {getMetadataDisplayInfo, getDuplicateIds} from "../../utils/metadata.utils";

function BuildingsEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {focusedBuilding} = useApp();

    return (
        <EntityEditor name={'Building'} entities={state.buildings.map((building): EntityProps => {
            return {
                id: building.id,
                statistics: [],
                attributes: building.attributes
            }
        })}
                      attributes={state.buildingAttributes}
                      focusedEntity={focusedBuilding}
                      addNewEntity={(name) => executeCommand(new AddBuildingCommand(name))}
                      deleteEntity={(id) => executeCommand(new RemoveBuildingCommand(id))}
                      deleteEntityAttribute={(id, attribute) => executeCommand(new RemoveBuildingAttributeCommand(id, attribute))}
                      addEntityAttribute={(id, attribute) => executeCommand(new AddBuildingAttributeCommand(id, attribute))}/>
    )
}

interface EntityEditorProps {
    name: string;
    entities: EntityProps[];
    attributes: Metadata[];
    focusedEntity?: { id: string };
    addNewEntity(name: string): void;
    deleteEntity(id: string): void;
    addEntityAttribute(id: string, attribute: string): void;
    deleteEntityAttribute(id: string, attribute: string): void;
}

export interface EntityProps {
    id: string;
    statistics: StatisticProps[];
    attributes: string[];
}

interface StatisticProps {
    name: string;
    value: number;
    onChange(value: number): void;
}

function EntityEditor(props: EntityEditorProps) {
    const {state} = useIntegrator();
    const {getLocalizationValue} = useApp();
    const [showPrompt, setShowPrompt] = useState(false);
    const dupes = getDuplicateIds(state.buildingAttributes);

    return (
        <div className={'layout horizontal'} style={{gap: 10, flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{flexGrow: 1, gap: 10}}>
                <List title={'Buildings'} elements={props.entities}
                      headerClassName={'box'}
                      component={element => <EntityItem entity={element} parent={props} dupes={dupes}/>}
                      search={element => getLocalizationValue(element.id)}
                      keyFor={element => element.id}
                />
                <button onClick={() => setShowPrompt(true)}>{`Create new ${props.name}`}</button>
                {showPrompt && <PromptDialog
                    title={`New ${props.name}`}
                    placeholder={`Enter ${props.name.toLowerCase()} name...`}
                    existingNames={props.entities.map(e => getLocalizationValue(e.id))}
                    onConfirm={(name) => { props.addNewEntity(name); setShowPrompt(false); }}
                    onCancel={() => setShowPrompt(false)}
                />}
            </div>
            <div className={'layout vertical'} style={{gap: 10, width: '25vw'}}>
                <div className={'layout vertical gap box flex-grow'}>
                    <List title={'Attributes'} elements={state.buildingAttributes}
                          component={element => <MetadataDragLabel uid={element.uid} id={element.id} values={element.values} duplicate={dupes.has(element.id)}/>}
                          search={element => getLocalizationValue(element.uid)}
                          keyFor={element => element.uid}
                    />
                </div>
                <div className={'layout vertical gap box flex-grow'}>
                    <List title={'Units'} elements={state.units}
                          component={element => <EntityDragLabel id={element.id} type={'unit'}/>}
                          search={element => getLocalizationValue(element.id)}
                          keyFor={element => element.id}
                    />
                </div>
            </div>
        </div>
    )
}

function EntityItem(props: { entity: EntityProps, parent: EntityEditorProps, dupes: Set<string> }) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, confirmDelete} = useApp();
    const deleteDisabled = props.parent.entities.length <= 1;

    const building = state.buildings.find(b => b.id === props.entity.id);

    const [open, setOpen] = useState(false);
    const rowRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (props.parent.focusedEntity?.id === props.entity.id) {
            setOpen(true);
            rowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [props.parent.focusedEntity, props.entity.id]);

    function checkCanDrop(monitor: DropTargetMonitor<any, void>): boolean {
        if (!monitor.isOver({shallow: true})) {
            return false;
        }

        if (building === undefined) {
            return false;
        }

        const type = monitor.getItemType();
        const item = monitor.getItem();
        switch (type) {
            case 'attribute':
                return !building.attributes?.includes(item.attribute);
            case 'unit':
                return !building.produceableUnits?.includes(item.id);
            default:
                return false;
        }
    }

    const [{canDrop}, drop] = useDrop({
        accept: ['attribute', 'unit'],
        drop: (item: any, monitor) => {
            if (building === undefined || !checkCanDrop(monitor)) {
                return;
            }

            switch (monitor.getItemType()) {
                case 'attribute':
                    building.attributes ??= [];
                    building.attributes.push(item.attribute);
                    break;
                case 'unit':
                    building.produceableUnits ??= [];
                    building.produceableUnits.push(item.id);
                    break;
                default:
                    return;
            }

            executeCommand(new UpdateBuildingCommand(building));
        },
        collect: (monitor) => {
            return {
                canDrop: checkCanDrop(monitor)
            };
        },
    });

    if (building === undefined) {
        return <></>
    }

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
            <ImagePackElement id={props.entity.id} level={building.startLevel} size={80}/>
            <LocalizationInput style={{width: 100}} id={props.entity.id}/>
            <BooleanInput value={building.active} onChange={(value) => {
                building.active = value;
                executeCommand(new UpdateBuildingCommand(building));
            }}/>
            <IconButton icon={'trash'} style={{opacity: deleteDisabled ? 0.5 : 1}} onClick={async (e) => {
                e.stopPropagation();
                if (!deleteDisabled && await confirmDelete(getLocalizationValue(props.entity.id), 'building')) {
                    props.parent.deleteEntity(props.entity.id);
                }
            }}/>
        </div>
    );

    return (
        <div ref={(el) => { drop(el); rowRef.current = el; }} className={'box transition'} style={{
            opacity: building.active ? 1 : 0.5,
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`
        }}>
            <Collapsible title={header} defaultOpen={false} open={open} onOpenChange={setOpen}>
                <div className={'layout vertical gap'} style={{gap: 6, marginTop: 10}}>
                    <p className={'text header'}>Description</p>
                    <LocalizationTextArea id={`${props.entity.id}.description`} style={{minHeight: 50}}/>
                </div>
                <div className={'layout horizontal gap'} style={{gap: 20, marginTop: 10}}>
                    <div className={'layout vertical flex-grow'} style={{gap: 10}}>
                        <div className={'layout vertical gap'}
                             style={{
                                 borderRadius: '5px',
                                 flexGrow: 1, overflowY: 'scroll', height: 0,
                                 paddingRight: 12,
                             }}>
                            <p className={'text header'}>Produceable Units</p>
                            {building?.produceableUnits?.length > 0
                                ? [...building.produceableUnits]
                                    .sort((a, b) => getLocalizationValue(a).localeCompare(getLocalizationValue(b)))
                                    .map((unit, i) => {
                                        return <EntityLabel key={i} id={unit} onDelete={() => {
                                            if (building === undefined) return;
                                            building.produceableUnits = building.produceableUnits.filter(t => t !== unit);
                                            executeCommand(new UpdateBuildingCommand(building));
                                        }}/>
                                    })
                                : <p className={'muted-text'}>None</p>
                            }
                            <p className={'text header'}>Attributes</p>
                            {props.entity.attributes.length > 0
                                ? [...props.entity.attributes]
                                    .sort((a, b) => getLocalizationValue(a).localeCompare(getLocalizationValue(b)))
                                    .map((uid, i) => {
                                        const meta = state.buildingAttributes?.find(m => m.uid === uid);
                                        const dup = props.dupes.has(meta?.id ?? '');
                                        const {suffix, tooltip} = getMetadataDisplayInfo(meta, dup, getLocalizationValue);
                                        return <div key={i} title={tooltip}><MetadataLabel uid={uid} suffix={suffix} onDelete={() => {
                                            props.parent.deleteEntityAttribute(props.entity.id, uid);
                                        }}/></div>;
                                    })
                                : <p className={'muted-text'}>None</p>
                            }
                        </div>
                    </div>
                    <div className={'layout vertical flex-grow'} style={{gap: 20}}>
                        <p className={'text header'}>Statistics</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel title={'Price in Gold'}
                                              tooltip={`The player has to spent ${building.price} gold to place this building.`}
                                              style={{flexGrow: 'unset'}}
                                              value={building.price}
                                              onChange={(value: number) => {
                                                  building.price = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Player Score'}
                                              tooltip={`How much this building contributes to the Players score.`}
                                              style={{flexGrow: 'unset'}}
                                              value={building.score}
                                              onChange={(value: number) => {
                                                  building.score = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Vision Range'} style={{flexGrow: 'unset'}}
                                              value={building.properties.visionRange}
                                              onChange={(value: number) => {
                                                  building.properties.visionRange = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Territory Range'} style={{flexGrow: 'unset'}}
                                              value={building.properties.territoryRange}
                                              onChange={(value: number) => {
                                                  building.properties.territoryRange = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Starting Slots'} style={{flexGrow: 'unset'}}
                                              value={building.properties.startingSlots}
                                              onChange={(value: number) => {
                                                  building.properties.startingSlots = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Max per City'} style={{flexGrow: 'unset'}}
                                              tooltip={'The max amount only applies if this value is above 0.'}
                                              value={building.maxPerCity}
                                              onChange={(value: number) => {
                                                  building.maxPerCity = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Max per Player'} style={{flexGrow: 'unset'}}
                                              tooltip={'The max amount only applies if this value is above 0.'}
                                              value={building.maxPerPlayer}
                                              onChange={(value: number) => {
                                                  building.maxPerPlayer = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                        </div>
                        <p className={'text header'}>Levels</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel title={'Start Level'} tooltip={`The level that this building starts with.`}
                                              style={{flexGrow: 'unset'}}
                                              value={building.startLevel}
                                              onChange={(value: number) => {
                                                  building.startLevel = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                            <NumberInputLabel title={'Max Level'}
                                              tooltip={`Caps total reward triggers across all passive rewards. 0 = no cap.`}
                                              style={{flexGrow: 'unset'}}
                                              value={building.maxLevel}
                                              onChange={(value: number) => {
                                                  building.maxLevel = value;
                                                  executeCommand(new UpdateBuildingCommand(building));
                                              }}
                            />
                        </div>
                        <p className={'text header'}>Rewards</p>
                        <div className={'layout vertical gap'} style={{gap: 6}}>
                            {(building.passiveRewards ?? []).map(reward => (
                                <PassiveRewardCard key={reward.uid} buildingId={building.id} reward={reward}/>
                            ))}
                            <button onClick={() => executeCommand(new AddBuildingPassiveRewardCommand(building.id))}>
                                <span className={'fa-solid fa-plus'} style={{marginRight: 6}}/>
                                Add Reward
                            </button>
                        </div>
                    </div>
                </div>
            </Collapsible>
        </div>
    )
}

function MetadataDragLabel(props: { uid: string, id: string, values?: { [key: string]: number }, duplicate?: boolean }) {
    const {getLocalizationValue} = useApp();
    const [{opacity}, drag] = useDrag({
        type: 'attribute',
        item: {
            attribute: props.uid,
        },
        collect: (monitor) => {
            const isDragging = monitor.isDragging();
            const opacity = isDragging ? 0.5 : 1;
            return {
                opacity: opacity,
            };
        },
    });

    const meta = props.id ? {id: props.id, values: props.values ?? {}} as any : undefined;
    const {suffix, tooltip} = getMetadataDisplayInfo(meta, props.duplicate ?? false, getLocalizationValue);

    return (
        <div ref={drag} style={{opacity}} title={tooltip}>
            <MetadataLabel uid={props.uid} suffix={suffix}/>
        </div>
    );
}

export default BuildingsEditorView;